/**
 * theme.Product
 * Handles product page functionality including:
 * - Variant selection and updates
 * - Image galleries (ProductGallery with Splide)
 * - Image set filtering
 * - 3D model media
 * - Quick view modal
 * - Store availability
 * - Inventory management
 * 
 * Note: Video handling is now done by component-product-gallery.js
 */
console.log('📦 theme.Product loaded');
theme.Product = (function () {
  var classes = {
    onSale: 'on-sale',
    disabled: 'disabled',
    isModal: 'is-modal',
    loading: 'loading',
    loaded: 'loaded',
    hidden: 'hide',
    interactable: 'video-interactable',
    visuallyHide: 'visually-invisible',
    lowInventory: 'inventory--low',
  };

  var selectors = {
    variantType: '.variant-wrapper',
    blocks: '[data-product-blocks]',
    blocksHolder: '[data-blocks-holder]',
    dynamicVariantsEnabled: '[data-dynamic-variants-enabled]',
  };

  function Product(container) {
    this.container = container;
    var sectionId = this.sectionId = container.getAttribute('data-section-id');
    var productId = this.productId = container.getAttribute('data-product-id');

    this.inModal = (container.dataset.modal === 'true');
    this.modal;

    this.settings = {
      enableHistoryState: container.dataset.history || false,
      namespace: '.product-' + sectionId,
      inventory: false,
      inventoryThreshold: 10,
      modalInit: false,
      hasImages: true,
      imageSetName: null,
      imageSetIndex: null,
      currentImageSet: null,
      imageSize: '620x'
    };

    // Overwrite some settings when loaded in modal
    if (this.inModal) {
      this.settings.enableHistoryState = false;
      this.settings.namespace = '.product-' + sectionId + '-modal';
      this.modal = document.getElementById('QuickShopModal-' + productId);
    }

    this.selectors = {
      variantsJson: '[data-variant-json]',
      currentVariantJson: '[data-current-variant-json]',
      form: '.product-single__form',

      media: '[data-product-media-type-model]',
      closeMedia: '.product-single__close-media',
      photoThumbs: '[data-product-thumb]',
      thumbSlider: '[data-product-thumbs]',
      thumbScroller: '.product__thumbs--scroller',
      mainSlider: '[data-product-photos]',
      imageContainer: '[data-product-images]',
      productImageMain: '[data-product-image-main]',

      priceWrapper: '[data-product-price-wrap]',
      price: '[data-product-price]',
      comparePrice: '[data-compare-price]',
      savePrice: '[data-save-price]',
      priceA11y: '[data-a11y-price]',
      comparePriceA11y: '[data-compare-price-a11y]',
      unitWrapper: '[data-unit-price-wrapper]',
      unitPrice: '[data-unit-price]',
      unitPriceBaseUnit: '[data-unit-base]',
      sku: '[data-sku]',
      inventory: '[data-product-inventory]',
      incomingInventory: '[data-incoming-inventory]',
      colorLabel: '[data-variant-color-label]',

      addToCart: '[data-add-to-cart]',
      addToCartText: '[data-add-to-cart-text]',
      backInStock: '[data-back-in-stock]',

      originalSelectorId: '[data-product-select]',
      singleOptionSelector: '[data-variant-input]',
      variantColorSwatch: '.variant__input--color-swatch',
      dynamicVariantsEnabled: '[data-dynamic-variants-enabled]',

      availabilityContainer: '[data-store-availability-holder]'
    };

    this.cacheElements();

    this.firstProductImage = this.cache.mainSlider.querySelector('img');

    if (!this.firstProductImage) {
      this.settings.hasImages = false;
    }

    // Check for image sets in new ProductGallery system
    var galleryWrapper = this.container.querySelector('[data-product-gallery]');
    if (galleryWrapper && galleryWrapper.dataset.hasImageSets === 'true') {
      this.settings.imageSetName = galleryWrapper.dataset.imageSetOption;
      this.settings.imageSetIndex = galleryWrapper.dataset.imageSetIndex;
      console.log('📦 Image sets detected from gallery wrapper:', {
        imageSetName: this.settings.imageSetName,
        imageSetIndex: this.settings.imageSetIndex
      });
    } else {
      // Fallback to old system
      var dataSetEl = this.cache.mainSlider.querySelector('[data-set-name]');
      if (dataSetEl) {
        this.settings.imageSetName = dataSetEl.dataset.setName;
      }
    }

    this.init();
  }

  Product.prototype = Object.assign({}, Product.prototype, {
    init: function () {
      if (this.inModal) {
        this.container.classList.add(classes.isModal);
        document.addEventListener('modalOpen.QuickShopModal-' + this.productId, this.openModalProduct.bind(this));
        document.addEventListener('modalClose.QuickShopModal-' + this.productId, this.closeModalProduct.bind(this));
      }

      if (!this.inModal) {
        this.formSetup();
        this.productSetup();
        this.customMediaListners();
        this.addIdToRecentlyViewed();
      }
    },

    cacheElements: function () {
      this.cache = {
        form: this.container.querySelector('form[id^="AddToCartForm"]'),
        mainSlider: this.container.querySelector(this.selectors.mainSlider),
        thumbSlider: this.container.querySelector(this.selectors.thumbSlider),
        thumbScroller: this.container.querySelector(this.selectors.thumbScroller),
        productImageMain: this.container.querySelector(this.selectors.productImageMain),

        // Price-related
        priceWrapper: this.container.querySelector(this.selectors.priceWrapper),
        comparePriceA11y: this.container.querySelector(this.selectors.comparePriceA11y),
        comparePrice: this.container.querySelector(this.selectors.comparePrice),
        price: this.container.querySelector(this.selectors.price),
        savePrice: this.container.querySelector(this.selectors.savePrice),
        priceA11y: this.container.querySelector(this.selectors.priceA11y)
      };
    },

    formSetup: function () {
      this.initQtySelector();
      this.initAjaxProductForm();
      this.availabilitySetup();
      this.initVariants();

      // We know the current variant now so setup image sets
      if (this.settings.imageSetName) {
        this.updateImageSet();
      }
    },

    availabilitySetup: function () {
      var container = this.container.querySelector(this.selectors.availabilityContainer);
      if (container) {
        this.storeAvailability = new theme.StoreAvailability(container);
      }
    },

    productSetup: function () {
      this.setImageSizes();
      this.initImageZoom();
      this.initModelViewerLibraries();
      this.initShopifyXrLaunch();
    },

    setImageSizes: function () {
      if (!this.settings.hasImages) {
        return;
      }

      // Get srcset image src, works on most modern browsers
      // otherwise defaults to settings.imageSize
      var currentImage = this.firstProductImage.currentSrc;

      if (currentImage) {
        this.settings.imageSize = theme.Images.imageSize(currentImage);
      }
    },

    addIdToRecentlyViewed: function () {
      var handle = this.container.getAttribute('data-product-handle');
      var url = this.container.getAttribute('data-product-url');
      var aspectRatio = this.container.getAttribute('data-aspect-ratio');
      var featuredImage = this.container.getAttribute('data-img-url');

      // Remove current product if already in set of recent
      if (theme.recentlyViewed.recent.hasOwnProperty(handle)) {
        delete theme.recentlyViewed.recent[handle];
      }

      // Add it back to the end
      theme.recentlyViewed.recent[handle] = {
        url: url,
        aspectRatio: aspectRatio,
        featuredImage: featuredImage
      };

      if (theme.config.hasLocalStorage) {
        window.localStorage.setItem('theme-recent', JSON.stringify(theme.recentlyViewed.recent));
      }
    },

    initVariants: function () {
      var variantJson = this.container.querySelector(this.selectors.variantsJson);

      if (!variantJson) {
        return;
      }

      this.variantsObject = JSON.parse(variantJson.innerHTML);
      var dynamicVariantsEnabled = !!this.container.querySelector(selectors.dynamicVariantsEnabled)

      var options = {
        container: this.container,
        enableHistoryState: this.settings.enableHistoryState,
        singleOptionSelector: this.selectors.singleOptionSelector,
        originalSelectorId: this.selectors.originalSelectorId,
        variants: this.variantsObject,
        dynamicVariantsEnabled
      };

      var swatches = this.container.querySelectorAll(this.selectors.variantColorSwatch);
      if (swatches.length) {
        swatches.forEach(swatch => {
          swatch.addEventListener('updateSwatch', function (evt) {
            var color = swatch.dataset.colorName;
            var index = swatch.dataset.colorIndex;
            this.updateColorName(color, index);
          }.bind(this))
        });
      }

      this.variants = new theme.Variants(options);

      // Product availability on page load
      if (this.storeAvailability) {
        var variant_id = this.variants.currentVariant ? this.variants.currentVariant.id : this.variants.variants[0].id;

        this.storeAvailability.updateContent(variant_id);
        this.container.on('variantChange' + this.settings.namespace, this.updateAvailability.bind(this));
      }

      this.container.on('variantChange' + this.settings.namespace, this.updateCartButton.bind(this));
      this.container.on('variantImageChange' + this.settings.namespace, this.updateVariantImage.bind(this));
      this.container.on('variantPriceChange' + this.settings.namespace, this.updatePrice.bind(this));
      this.container.on('variantUnitPriceChange' + this.settings.namespace, this.updateUnitPrice.bind(this));

      if (this.container.querySelector(this.selectors.sku)) {
        this.container.on('variantSKUChange' + this.settings.namespace, this.updateSku.bind(this));
      }

      var inventoryEl = this.container.querySelector(this.selectors.inventory);
      if (inventoryEl) {
        this.settings.inventory = true;
        this.settings.inventoryThreshold = inventoryEl.dataset.threshold;

        // Update inventory on page load
        this.updateInventory({ detail: { variant: this.variants.currentVariant } });

        this.container.on('variantChange' + this.settings.namespace, this.updateInventory.bind(this));
      }

      // Update individual variant availability on each selection
      if (dynamicVariantsEnabled) {
        var currentVariantJson = this.container.querySelector(this.selectors.currentVariantJson);

        if (currentVariantJson) {
          var variantType = this.container.querySelector(selectors.variantType);

          if (variantType) {
            new theme.VariantAvailability({
              container: this.container,
              namespace: this.settings.namespace,
              type: variantType.dataset.type,
              variantsObject: this.variantsObject,
              currentVariantObject: JSON.parse(currentVariantJson.innerHTML)
            });
          }
        }
      }

      // Register variant change listener - ProductGallery will handle gallery updates
      this.container.on('variantChange' + this.settings.namespace, this.updateImageSet.bind(this));
    },

    initQtySelector: function () {
      this.container.querySelectorAll('.js-qty__wrapper').forEach(el => {
        new theme.QtySelector(el, {
          namespace: '.product'
        });
      });
    },

    initAjaxProductForm: function () {
      if (theme.settings.cartType === 'drawer') {
        new theme.AjaxProduct(this.cache.form);
      }
    },

    /*============================================================================
      Variant change methods
    ==============================================================================*/
    updateColorName: function (color, index) {
      // Updates on radio button change, not variant.js
      this.container.querySelector(this.selectors.colorLabel + `[data-index="${index}"`).textContent = color;
    },

    updateCartButton: function (evt) {
      var variant = evt.detail.variant;
      var cartBtn = this.container.querySelector(this.selectors.addToCart);
      var bisButton = this.container.querySelector(this.selectors.backInStock);
      var cartBtnText = this.container.querySelector(this.selectors.addToCartText);
      var buttonPrice = this.container.querySelector('[data-button-price]');

      if (!cartBtn) {
        if (bisButton) {
          bisButton.style.display = 'none';
        }
        return;
      }

      if (variant) {
        if (variant.available) {
          // Available, enable the submit button and change text
          cartBtn.classList.remove(classes.disabled);
          cartBtn.disabled = false;
          var defaultText = cartBtnText.dataset.defaultText;
          cartBtnText.textContent = defaultText;
          cartBtn.setAttribute('aria-label', cartBtnText.textContent);

          // Update button price
          if (buttonPrice) {
            buttonPrice.innerHTML = theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat);
            buttonPrice.style.display = '';
          }

          if (bisButton) {
            bisButton.dataset.variantId = variant.id;
            bisButton.style.display = 'none';
          }
        } else {
          // Sold out, disable the submit button and change text
          cartBtn.classList.add(classes.disabled);
          cartBtn.disabled = true;
          cartBtnText.textContent = theme.strings.soldOut;
          cartBtn.setAttribute('aria-label', cartBtnText.textContent);

          // Hide button price when sold out
          if (buttonPrice) {
            buttonPrice.style.display = 'none';
          }

          if (bisButton) {
            bisButton.dataset.variantId = variant.id;
            bisButton.style.display = '';
          }
        }
      } else {
        // The variant doesn't exist, disable submit button
        cartBtn.classList.add(classes.disabled);
        cartBtn.disabled = true;
        cartBtnText.textContent = theme.strings.unavailable;
        cartBtn.setAttribute('aria-label', cartBtnText.textContent);

        // Hide button price when unavailable
        if (buttonPrice) {
          buttonPrice.style.display = 'none';
        }
        if (bisButton) {
          bisButton.dataset.variantId = variant.id;
          bisButton.style.display = '';
        }
      }
    },

    updatePrice: function (evt) {
      console.log('Updating price...');
      var variant = evt.detail.variant;

      if (!variant) {
        return;
      }

      // Storewide discount (Theme settings > Products) mirrors component-product-price.liquid
      var price = variant.price;
      var compareAtPrice = variant.compare_at_price || 0;
      var storewideDiscount = Number(theme.settings.storewideDiscount) || 0;

      if (storewideDiscount > 0) {
        price = Math.round((variant.price * (100 - storewideDiscount)) / 100);
        compareAtPrice = Math.max(compareAtPrice, variant.price);
      }

      var priceBlocks = this.container.querySelectorAll('.product-main-form__block--price .product-price');

      if (!priceBlocks.length && this.cache.price) {
        priceBlocks = [this.cache.price.closest('.product-price')].filter(Boolean);
      }

      priceBlocks.forEach(function (priceBlock) {
        var priceEl = priceBlock.querySelector(this.selectors.price);
        if (!priceEl) {
          return;
        }

        // Regular price
        priceEl.innerHTML = theme.Currency.formatMoney(price, theme.settings.moneyFormat);

        var comparePriceEl = priceBlock.querySelector(this.selectors.comparePrice);
        var priceWrapperEl = priceBlock.querySelector(this.selectors.priceWrapper);
        var priceA11yEl = priceBlock.querySelector(this.selectors.priceA11y);
        var comparePriceA11yEl = priceBlock.querySelector(this.selectors.comparePriceA11y);
        var savePriceEl = priceBlock.querySelector(this.selectors.savePrice);
        var storewideBadgeEl = priceBlock.querySelector('[data-storewide-discount-badge-auto]');

        if (storewideBadgeEl && compareAtPrice > 0) {
          storewideBadgeEl.textContent = Math.round(((compareAtPrice - price) * 100) / compareAtPrice) + '% off';
        }

        // Sale price, if necessary
        if (compareAtPrice > price) {
          if (comparePriceEl) {
            comparePriceEl.innerHTML = theme.Currency.formatMoney(compareAtPrice, theme.settings.moneyFormat);
          }
          if (priceWrapperEl) {
            priceWrapperEl.classList.remove(classes.hidden);
          }
          priceEl.classList.add(classes.onSale);
          if (comparePriceA11yEl) {
            comparePriceA11yEl.setAttribute('aria-hidden', 'false');
          }
          if (priceA11yEl) {
            priceA11yEl.setAttribute('aria-hidden', 'false');
          }

          var savings = compareAtPrice - price;

          if (theme.settings.saveType == 'percent') {
            savings = Math.round(((savings) * 100) / compareAtPrice) + '%';
          } else {
            savings = theme.Currency.formatMoney(savings, theme.settings.moneyFormat);
          }

          if (savePriceEl) {
            savePriceEl.classList.remove(classes.hidden);
            savePriceEl.innerHTML = theme.strings.savePrice.replace('[saved_amount]', savings);
          }
        } else {
          if (priceWrapperEl) {
            priceWrapperEl.classList.add(classes.hidden);
          }
          if (savePriceEl) {
            savePriceEl.classList.add(classes.hidden);
          }
          priceEl.classList.remove(classes.onSale);
          if (comparePriceA11yEl) {
            comparePriceA11yEl.setAttribute('aria-hidden', 'true');
          }
          if (priceA11yEl) {
            priceA11yEl.setAttribute('aria-hidden', 'true');
          }
        }
      }.bind(this));
    },

    updateUnitPrice: function (evt) {
      var variant = evt.detail.variant;

      if (variant && variant.unit_price) {
        this.container.querySelector(this.selectors.unitPrice).innerHTML = theme.Currency.formatMoney(variant.unit_price, theme.settings.moneyFormat);
        this.container.querySelector(this.selectors.unitPriceBaseUnit).innerHTML = theme.Currency.getBaseUnit(variant);
        this.container.querySelector(this.selectors.unitWrapper).classList.remove(classes.hidden);
      } else {
        this.container.querySelector(this.selectors.unitWrapper).classList.add(classes.hidden);
      }
    },

    updateImageSet: function (evt) {
      var variant = evt && evt.detail ? evt.detail.variant : null;

      if (!variant && this.variants) {
        variant = this.variants.currentVariant;
      }

      if (!variant) {
        return;
      }

      var galleryWrapper = this.container.querySelector('[data-product-gallery]');

      if (!galleryWrapper) {
        return;
      }

      var callRender = function (instance) {
        if (instance && typeof instance.renderVariant === 'function') {
          instance.renderVariant(variant);
        }
      };

      if (galleryWrapper._productGalleryInstance) {
        callRender(galleryWrapper._productGalleryInstance);
        return;
      }

      // If the gallery has not finished initializing, wait for the ready event once
      var onReady = function (event) {
        if (!event || !event.detail || !event.detail.instance) {
          return;
        }

        galleryWrapper.removeEventListener('productGallery:ready', onReady);
        callRender(event.detail.instance);
      };

      galleryWrapper.addEventListener('productGallery:ready', onReady, { once: true });
    },

    getImageSetName: function (string) {
      return string.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '');
    },

    updateSku: function (evt) {
      var variant = evt.detail.variant;
      var newSku = '';

      if (variant) {
        if (variant.sku) {
          newSku = variant.sku;
        }

        this.container.querySelector(this.selectors.sku).textContent = newSku;
      }
    },

    updateInventory: function (evt) {
      var variant = evt.detail.variant;

      // Override to display in stock message for sold out variants with policy set to continue
      // Must be set to true in order to display in stock message for sold out variants
      const inStockForOOSAndContinueSelling = false;

      if (!variant) {
        // Variant is unavailable
        // So we want to hide both the inventory quantity + incoming transfer notice
        this.toggleInventoryQuantity('hidden', false);
        this.toggleIncomingInventory(false);
        return;
      }

      // Inventory management is nil
      // So we want to hide the low inventory message but show an In stock status
      // And hide the incoming transfer notice
      if (!variant.inventory_management) {
        this.toggleInventoryQuantity('visible', false);
        this.toggleIncomingInventory(false);
        return;
      }

      if (variant.inventory_management === 'shopify' && window.inventories && window.inventories[this.productId]) {
        const variantInventoryObject = window.inventories[this.productId][variant.id];

        const { quantity, policy, incoming, next_incoming_date } = variantInventoryObject || {};

        this.toggleInventoryQuantity(undefined, quantity);

        if (inStockForOOSAndContinueSelling) {
          if (quantity <= 0 && policy === 'continue') {
            this.toggleInventoryQuantity('visible', false);
            this.toggleIncomingInventory(false);
            return;
          }
        }

        if ((incoming && !variant.available) || (quantity <= 0 && policy === 'continue')) {
          this.toggleIncomingInventory(true, next_incoming_date, policy);
        } else {
          this.toggleIncomingInventory(false);
        }
      }
    },

    updateAvailability: function (evt) {
      var variant = evt.detail.variant;
      if (!variant) {
        return;
      }

      this.storeAvailability.updateContent(variant.id);
    },

    toggleInventoryQuantity: function (state = undefined, quantity) {
      const productInventoryEl = this.container.querySelector(this.selectors.inventory);
      const inventorySalesPoint = productInventoryEl.closest('.sales-point');

      if (state && state === 'hidden') {
        // variant is unavailable
        // hide and return
        if (inventorySalesPoint) {
          inventorySalesPoint.classList.add(classes.hidden);
        }

        return;
      }

      let showLowInventoryMessage = false;

      // Check if we should show low inventory message
      if (parseInt(quantity) <= parseInt(this.settings.inventoryThreshold) && parseInt(quantity) > 0) {
        showLowInventoryMessage = true;
      }

      if (parseInt(quantity) > 0 || (state && state === 'visible')) {
        if (showLowInventoryMessage) {
          productInventoryEl.parentNode.classList.add(classes.lowInventory);
          if (quantity > 1) {
            productInventoryEl.textContent = theme.strings.otherStockLabel.replace('[count]', quantity);
          } else {
            productInventoryEl.textContent = theme.strings.oneStockLabel.replace('[count]', quantity);
          }
        } else {
          productInventoryEl.parentNode.classList.remove(classes.lowInventory);
          productInventoryEl.textContent = theme.strings.inStockLabel;
        }

        if (inventorySalesPoint) {
          inventorySalesPoint.classList.remove(classes.hidden);
        }

      } else {
        if (inventorySalesPoint) {
          inventorySalesPoint.classList.add(classes.hidden);
        }
      }
    },

    toggleIncomingInventory: function (showIncomingInventory, incomingInventoryDate, policy) {

      const incomingInventoryEl = this.container.querySelector(this.selectors.incomingInventory);
      const incomingInventoryIcon = incomingInventoryEl.querySelector('.icon-and-text');

      if (!incomingInventoryEl) return;

      const incomingInventoryBlockEnabled = incomingInventoryEl.dataset.enabled === 'true';
      const textEl = incomingInventoryEl.querySelector('.js-incoming-text');

      // If incoming inventory block is disabled, hide it
      if (!incomingInventoryBlockEnabled) {
        incomingInventoryEl.classList.add(classes.hidden);
        return;
      }

      if (showIncomingInventory) {
        if (incomingInventoryDate) {
          textEl.textContent = theme.strings.willBeInStockAfter.replace('[date]', incomingInventoryDate);
          incomingInventoryEl.classList.remove(classes.hidden);
        } else {
          textEl.textContent = theme.strings.waitingForStock;
          incomingInventoryEl.classList.remove(classes.hidden);
        }

        // When OOS and incoming inventory and continue selling disabled, update icon to low inventory
        if (incomingInventoryIcon) {
          if (policy !== 'continue') {
            incomingInventoryIcon.classList.add(classes.lowInventory);
          } else {
            incomingInventoryIcon.classList.remove(classes.lowInventory);
          }
        }

      } else {
        incomingInventoryEl.classList.add(classes.hidden);
      }
    },

    /*============================================================================
      Product videos - Now handled by component-product-gallery.js
    ==============================================================================*/

    /*============================================================================
      Product images
    ==============================================================================*/
    initImageZoom: function () {
      var container = this.container.querySelector(this.selectors.imageContainer);
      if (!container) {
        return;
      }
      var imageZoom = new theme.Photoswipe(container, this.sectionId);
      container.addEventListener('photoswipe:afterChange', function (evt) {
        // Sync gallery with photoswipe
        var galleryWrapper = this.container.querySelector('[data-product-gallery]');
        if (galleryWrapper && galleryWrapper._productGalleryInstance) {
          var gallery = galleryWrapper._productGalleryInstance;
          if (gallery.mainSplide) {
            gallery.mainSplide.go(evt.detail.index);
          }
        }
      }.bind(this));
    },

    getThumbIndex: function (target) {
      return target.dataset.index;
    },

    updateVariantImage: function (evt) {
      // ProductGallery handles all image switching via variantChange event
    },

    /*============================================================================
      Product media (3D)
    ==============================================================================*/

    /*============================================================================
      Products when in quick view modal
    ==============================================================================*/
    openModalProduct: function () {
      var initialized = false;

      if (!this.settings.modalInit) {
        this.blocksHolder = this.container.querySelector(selectors.blocksHolder);
        var url = this.blocksHolder.dataset.url;

        fetch(url).then(function (response) {
          return response.text();
        }).then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var blocks = doc.querySelector(selectors.blocks);

          // Because the same product could be opened in quick view
          // on the page we load the form elements from, we need to
          // update any `id`, `for`, and `form` attributes
          blocks.querySelectorAll('[id]').forEach(el => {
            // Update input `id`
            var val = el.getAttribute('id');
            el.setAttribute('id', val + '-modal');

            // Update related label if it exists
            var label = blocks.querySelector(`[for="${val}"]`);
            if (label) {
              label.setAttribute('for', val + '-modal');
            }

            // Update any collapsible elements
            var collapsibleTrigger = blocks.querySelector(`[aria-controls="${val}"]`);
            if (collapsibleTrigger) {
              collapsibleTrigger.setAttribute('aria-controls', val + '-modal');
            }
          });

          // Update any elements with `form` attribute
          var form = blocks.querySelector(this.selectors.form);
          var formId = form.getAttribute('id');
          blocks.querySelectorAll('[form]').forEach(el => {
            el.setAttribute('form', formId + '-modal');
          });

          this.blocksHolder.innerHTML = '';
          this.blocksHolder.append(blocks);
          this.blocksHolder.classList.add('product-form-holder--loaded');

          this.cacheElements();

          this.formSetup();
          this.updateModalProductInventory();

          if (Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          // Re-hook up collapsible box triggers
          theme.collapsibles.init(this.container);

          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: {
              productId: this.sectionId
            }
          }));
        }.bind(this));

        this.productSetup();

        // ProductGallery handles slider initialization and videos
        this.customMediaListners();
        this.addIdToRecentlyViewed();
        this.settings.modalInit = true;
      } else {
        initialized = true;
      }

      AOS.refreshHard();

      document.dispatchEvent(new CustomEvent('quickview:open', {
        detail: {
          initialized: initialized,
          productId: this.sectionId
        }
      }));
    },

    // Recommended products load via JS and don't add variant inventory to the
    // global variable that we later check. This function scrapes a data div
    // to get that info and manually add the values.
    updateModalProductInventory: function () {
      window.inventories = window.inventories || {};
      this.container.querySelectorAll('.js-product-inventory-data').forEach(el => {
        var productId = el.dataset.productId;
        window.inventories[productId] = {};

        el.querySelectorAll('.js-variant-inventory-data').forEach(el => {
          window.inventories[productId][el.dataset.id] = {
            'quantity': el.dataset.quantity,
            'policy': el.dataset.policy,
            'incoming': el.dataset.incoming,
            'next_incoming_date': el.dataset.date
          }
        });
      });
    },

    closeModalProduct: function () {
      // Video cleanup is handled by ProductGallery component
    },

    /*============================================================================
      Product media (3D)
    ==============================================================================*/
    initModelViewerLibraries: function () {
      var modelViewerElements = this.container.querySelectorAll(this.selectors.media);
      if (modelViewerElements.length < 1) return;

      theme.ProductMedia.init(modelViewerElements, this.sectionId);
    },

    initShopifyXrLaunch: function () {
      document.addEventListener(
        'shopify_xr_launch',
        function () {
          var currentMedia = this.container.querySelector(
            this.selectors.productMediaWrapper +
            ':not(.' +
            self.classes.hidden +
            ')'
          );
          currentMedia.dispatchEvent(
            new CustomEvent('xrLaunch', {
              bubbles: true,
              cancelable: true
            })
          );
        }.bind(this)
      );
    },

    customMediaListners: function () {
      document.querySelectorAll(this.selectors.closeMedia).forEach(el => {
        el.addEventListener('click', function () {
          // Gallery will handle media state
        }.bind(this))
      });

      var modelViewers = this.container.querySelectorAll('model-viewer');
      if (modelViewers.length) {
        modelViewers.forEach(el => {
          el.addEventListener('shopify_model_viewer_ui_toggle_play', function (evt) {
            this.mediaLoaded(evt);
          }.bind(this));

          el.addEventListener('shopify_model_viewer_ui_toggle_pause', function (evt) {
            this.mediaUnloaded(evt);
          }.bind(this));
        });
      }
    },

    mediaLoaded: function (evt) {
      this.container.querySelectorAll(this.selectors.closeMedia).forEach(el => {
        el.classList.remove(classes.hidden);
      });
    },

    mediaUnloaded: function (evt) {
      this.container.querySelectorAll(this.selectors.closeMedia).forEach(el => {
        el.classList.add(classes.hidden);
      });
    },

    onUnload: function () {
      theme.ProductMedia.removeSectionModels(this.sectionId);
    }
  });

  return Product;
})();
